import React, { useState, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { authenticatedFetch, authenticatedFetchJson } from '../../../services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import logger from '../../../utils/logger'
import styles from '../AdminBillingPendingReview.module.scss'

/**
 * Download a CSV blob from a fetch Response.
 */
function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
}

const GenerateDialog = ({ onClose, onComplete, showToast }) => {
  const [dryRun, setDryRun] = useState(false)
  const [campaigns, setCampaigns] = useState([])
  const [campaignsLoading, setCampaignsLoading] = useState(false)
  const [campaignsError, setCampaignsError] = useState(null)
  const [selectedCampaign, setSelectedCampaign] = useState(null)
  const [query, setQuery] = useState('')
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [billingMonth, setBillingMonth] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setCampaignsLoading(true)
    setCampaignsError(null)
    authenticatedFetchJson(apiEndpoints.billingBillableCampaigns)
      .then((res) => {
        if (cancelled) return
        const rows = res?.data || res || []
        setCampaigns(Array.isArray(rows) ? rows : [])
      })
      .catch((err) => {
        if (cancelled) return
        logger.error('billable-campaigns lookup failed', err)
        setCampaignsError(err.message || 'Failed to load campaigns')
      })
      .finally(() => {
        if (!cancelled) setCampaignsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const filteredCampaigns = (() => {
    if (!query.trim()) return campaigns.slice(0, 50)
    const q = query.trim().toLowerCase()
    const startsWith = []
    const contains = []
    for (const c of campaigns) {
      const n = (c.campaign_name || '').toLowerCase()
      if (n.startsWith(q)) startsWith.push(c)
      else if (n.includes(q)) contains.push(c)
    }
    return [...startsWith, ...contains].slice(0, 50)
  })()

  const submit = useCallback(async () => {
    setSubmitting(true)
    try {
      const body = {}
      if (dryRun) body.dry_run = true
      if (selectedCampaign) body.campaign_id = selectedCampaign.id
      if (billingMonth.trim()) body.billing_month = billingMonth.trim()
      const res = await authenticatedFetchJson(apiEndpoints.billingGenerateMonthly, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const counts = res?.data || res || {}
      const base = counts.baseDrafted ?? counts.base_drafted ?? 0
      const overage = counts.overageDrafted ?? counts.overage_drafted ?? 0
      const planned = counts.dryRunPlanned ?? 0
      const skipped = counts.skipped ?? 0
      const alreadyExisted = counts.alreadyExisted ?? 0
      const errors = counts.errors ?? 0
      const monthSuffix = billingMonth.trim() ? ` for ${billingMonth.trim()}` : ''
      const existedSuffix = alreadyExisted > 0 ? `, ${alreadyExisted} already existed (kept)` : ''
      const summary = dryRun
        ? `[Dry-run] Would draft ${planned} campaign(s)${monthSuffix} (${skipped} skipped, ${errors} errors)`
        : `Drafted ${base} base + ${overage} overage invoices${monthSuffix} (${skipped} skipped${existedSuffix}, ${errors} errors)`
      showToast(summary, errors > 0 ? 'warning' : 'success')
      onComplete()
    } catch (err) {
      logger.error('generate-monthly failed', err)
      showToast(err.message || 'Generation failed', 'error')
    } finally {
      setSubmitting(false)
    }
  }, [dryRun, selectedCampaign, billingMonth, showToast, onComplete])

  return (
    <div className={styles.opsDialog}>
      <h4>Generate monthly invoices</h4>
      <p>
        Bills the selected month in arrears — base + overage invoices for the same period. Existing
        invoices for the period are left alone (idempotent).
      </p>
      <label htmlFor="ops-dryrun">
        <input
          id="ops-dryrun"
          type="checkbox"
          checked={dryRun}
          onChange={(e) => setDryRun(e.target.checked)}
        />
        Dry run (no DB writes)
      </label>
      <div className={styles.opsCampaignPicker} style={{ marginTop: 8 }}>
        <label htmlFor="ops-campaign">Restrict to campaign (optional)</label>
        {selectedCampaign ? (
          <div className={styles.opsCampaignChip} data-testid="ops-generate-campaign-chip">
            <span>
              <strong>{selectedCampaign.campaign_name}</strong>
              {selectedCampaign.currency ? ` · ${selectedCampaign.currency}` : ''}
              {selectedCampaign.plan_level_name ? ` · ${selectedCampaign.plan_level_name}` : ''}
            </span>
            <button
              type="button"
              onClick={() => {
                setSelectedCampaign(null)
                setQuery('')
                setDropdownOpen(false)
              }}
              aria-label="Clear selected campaign"
              data-testid="ops-generate-campaign-clear"
            >
              ×
            </button>
          </div>
        ) : (
          <div className={styles.opsCampaignSearchWrap}>
            <input
              id="ops-campaign"
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                setDropdownOpen(true)
              }}
              onFocus={() => setDropdownOpen(true)}
              onBlur={() => {
                setTimeout(() => setDropdownOpen(false), 120)
              }}
              placeholder={
                campaignsLoading
                  ? 'Loading campaigns…'
                  : campaignsError
                    ? 'Failed to load — retry by reopening dialog'
                    : 'Search by campaign name…'
              }
              disabled={campaignsLoading || !!campaignsError}
              autoComplete="off"
              data-testid="ops-generate-campaign-input"
            />
            {dropdownOpen && filteredCampaigns.length > 0 && (
              <ul
                className={styles.opsCampaignDropdown}
                data-testid="ops-generate-campaign-dropdown"
              >
                {filteredCampaigns.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setSelectedCampaign(c)
                        setQuery('')
                        setDropdownOpen(false)
                      }}
                      data-testid={`ops-generate-campaign-option-${c.id}`}
                    >
                      <span className={styles.opsCampaignOptionName}>{c.campaign_name}</span>
                      <span className={styles.opsCampaignOptionMeta}>
                        #{c.id}
                        {c.currency ? ` · ${c.currency}` : ''}
                        {c.plan_level_name ? ` · ${c.plan_level_name}` : ''}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {dropdownOpen &&
              !campaignsLoading &&
              !campaignsError &&
              filteredCampaigns.length === 0 && (
                <div className={styles.opsCampaignDropdownEmpty}>No matches</div>
              )}
          </div>
        )}
        <small style={{ display: 'block', marginTop: 4, opacity: 0.75 }}>
          {selectedCampaign
            ? 'Only this campaign will be processed.'
            : 'Leave blank to process every active billable campaign (cron default).'}
        </small>
      </div>
      <div style={{ marginTop: 8 }}>
        <label htmlFor="ops-billing-month">Billing month (optional)</label>
        <input
          id="ops-billing-month"
          type="month"
          value={billingMonth}
          onChange={(e) => setBillingMonth(e.target.value)}
          data-testid="ops-generate-billing-month-input"
        />
        <small style={{ display: 'block', marginTop: 4, opacity: 0.75 }}>
          {billingMonth.trim()
            ? `Base + overage invoices will both cover ${billingMonth.trim()}. Existing invoices for this period will not be regenerated.`
            : 'Leave blank to bill the month that just ended (cron default). Set to YYYY-MM to replay a different month.'}
        </small>
      </div>
      <div className={styles.opsDialogActions}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={submit}
          disabled={submitting}
          data-testid="ops-generate-submit"
        >
          {submitting ? 'Running…' : dryRun ? 'Run Dry-run' : 'Run Generation'}
        </button>
      </div>
    </div>
  )
}
GenerateDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

const ManualAchDialog = ({ onClose, showToast }) => {
  const [password, setPassword] = useState('')
  const [includeAlreadyCharged, setIncludeAlreadyCharged] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const submit = useCallback(async () => {
    if (!password) {
      showToast('Export password is required', 'error')
      return
    }
    setSubmitting(true)
    try {
      const res = await authenticatedFetch(apiEndpoints.billingExportsManualAch, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password,
          include_already_charged: includeAlreadyCharged,
        }),
      })
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Export failed (${res.status})`)
      }
      const blob = await res.blob()
      downloadBlob(blob, `manual-ach-charges-${new Date().toISOString().split('T')[0]}.csv`)
      showToast('Manual ACH export downloaded', 'success')
      onClose()
    } catch (err) {
      logger.error('manual-ach export failed', err)
      showToast(err.message || 'Export failed', 'error')
    } finally {
      setSubmitting(false)
    }
  }, [password, includeAlreadyCharged, showToast, onClose])

  return (
    <div className={styles.opsDialog}>
      <h4>Export manual ACH charge file</h4>
      <p>
        Lists every finalized invoice whose customer has US bank details on file. The file contains
        decrypted routing &amp; account numbers — handle with care.
      </p>
      <label htmlFor="ops-ach-pwd">Export password</label>
      <input
        id="ops-ach-pwd"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="off"
      />
      <label htmlFor="ops-ach-inc" style={{ marginTop: 8 }}>
        <input
          id="ops-ach-inc"
          type="checkbox"
          checked={includeAlreadyCharged}
          onChange={(e) => setIncludeAlreadyCharged(e.target.checked)}
        />
        Include already-paid invoices
      </label>
      <div className={styles.opsDialogActions}>
        <button
          type="button"
          className={styles.secondaryButton}
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="button"
          className={styles.primaryButton}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  )
}
ManualAchDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

/**
 * Parse a chunked text/event-stream body. Emits one decoded `data:` JSON
 * payload per onEvent call. Comment lines (`:keepalive ...`) are ignored.
 *
 * On stream end, flushes the TextDecoder (handles mid-multibyte chars at
 * chunk boundaries) and processes any trailing buffered frame so the final
 * event is never dropped if the server didn't terminate with `\n\n`.
 */
async function consumeSseStream(response, onEvent) {
  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  const processFrame = (frame) => {
    for (const line of frame.split('\n')) {
      if (line.startsWith('data:')) {
        const json = line.slice(5).trim()
        /* istanbul ignore else -- empty data lines are tolerated */
        if (json) {
          try {
            onEvent(JSON.parse(json))
          } catch (err) {
            logger.error('Bad SSE frame', err, json)
          }
        }
      }
    }
  }

  const drainCompleteFrames = () => {
    let sep
    while ((sep = buffer.indexOf('\n\n')) !== -1) {
      const frame = buffer.slice(0, sep)
      buffer = buffer.slice(sep + 2)
      processFrame(frame)
    }
  }

  try {
    let streaming = true
    while (streaming) {
      const { value, done } = await reader.read()
      if (done) {
        streaming = false
        break
      }
      buffer += decoder.decode(value, { stream: true })
      drainCompleteFrames()
    }

    buffer += decoder.decode()
    drainCompleteFrames()
    if (buffer.trim()) {
      processFrame(buffer)
    }
  } finally {
    try {
      reader.cancel().catch(() => {})
    } catch (_err) {
      /* reader already released */
    }
  }
}

const rowKey = (campaignId, periodStart) => `${campaignId}|${periodStart}`

const ReuploadDialog = ({ onClose, onComplete, showToast }) => {
  const [csv, setCsv] = useState('')
  const [filename, setFilename] = useState('')
  const [preview, setPreview] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [progress, setProgress] = useState(null)
  const [results, setResults] = useState(null)

  const onFile = useCallback((e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setPreview(null)
    setResults(null)
    setProgress(null)
    setFilename(file.name)
    const reader = new window.FileReader()
    reader.onload = () => setCsv(String(reader.result || ''))
    reader.readAsText(file)
  }, [])

  const runPreview = useCallback(async () => {
    /* istanbul ignore if -- defensive guard; the button is disabled until csv is set */
    if (!csv) {
      showToast('Pick a CSV first', 'error')
      return
    }
    setSubmitting(true)
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingDraftInvoicesReuploadPreview, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ csv }),
      })
      setPreview(res?.data || null)
    } catch (err) {
      logger.error('reupload-preview failed', err)
      showToast(err.message || 'Preview failed', 'error')
    } finally {
      setSubmitting(false)
    }
  }, [csv, showToast])

  const runCommit = useCallback(async () => {
    if (!preview?.csv_hash) return
    setSubmitting(true)

    try {
      const response = await authenticatedFetch(
        apiEndpoints.billingDraftInvoicesReuploadCommitStream,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
          body: JSON.stringify({ csv, csv_hash: preview.csv_hash }),
        },
      )

      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '')
        throw new Error(text || `Commit failed (${response.status})`)
      }

      setResults(new Map())
      setProgress({ processed: 0, total: preview.total })

      let summary = null
      let voidedOverage = 0
      let createdOverage = 0
      let revivedOverage = 0
      await consumeSseStream(response, (event) => {
        if (event.type === 'start') {
          setProgress({ processed: 0, total: event.total })
        } else if (event.type === 'row') {
          setProgress({ processed: event.processed, total: event.total })
          setResults((prev) => {
            const next = new Map(prev)
            next.set(rowKey(event.campaign_id, event.period_start), event)
            return next
          })
          if (event.after?.overage_action === 'void') voidedOverage++
          else if (event.after?.overage_action === 'create') createdOverage++
          else if (event.after?.overage_action === 'revive') revivedOverage++
        } else if (event.type === 'done') {
          summary = event
        } else if (event.type === 'error') {
          throw new Error(event.error || 'Stream error')
        }
      })

      if (summary) {
        const parts = [`Updated ${summary.committed} / ${summary.total} drafts on Stripe`]
        if (summary.failed) parts.push(`${summary.failed} failed`)
        if (voidedOverage) parts.push(`${voidedOverage} overage drafts voided`)
        if (createdOverage) parts.push(`${createdOverage} overage drafts created`)
        if (revivedOverage) parts.push(`${revivedOverage} overage drafts revived`)
        parts.push('still in draft — click Finalize All Drafts on the Dashboard tab once verified')
        const severity = summary.failed ? 'warning' : 'success'
        showToast(parts.join(' — '), severity)
        onComplete()
      } else {
        showToast('Commit ended without a "done" event — check server logs', 'warning')
      }
    } catch (err) {
      logger.error('reupload-commit-stream failed', err)
      showToast(err.message || 'Commit failed', 'error')
      setResults(null)
      setProgress(null)
    } finally {
      setSubmitting(false)
    }
  }, [csv, preview, showToast, onComplete])

  return (
    <div className={styles.opsDialog}>
      <h4>Re-upload tuned CSV</h4>
      <p>
        Upload the export after the team tunes the numbers. Each draft invoice is updated to match
        the row and re-pushed to Stripe as a draft. Invoices stay in <strong>draft</strong>
        status — finalize, email, and auto-charge happen later when you click <em>Approve</em>
        on the Dashboard tab.
      </p>
      <label htmlFor="ops-reupload-file">CSV file</label>
      <input id="ops-reupload-file" type="file" accept=".csv,text/csv" onChange={onFile} />
      {filename && (
        <p>
          Loaded: <strong>{filename}</strong>
        </p>
      )}

      {!preview && (
        <div className={styles.opsDialogActions}>
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className={styles.primaryButton}
            onClick={runPreview}
            disabled={submitting || !csv}
          >
            {submitting ? 'Validating…' : 'Preview Changes'}
          </button>
        </div>
      )}

      {preview && !results && (
        <>
          <p>
            <strong>{preview.total}</strong> rows parsed,
            <strong> {preview.changed}</strong> with changes,
            <strong> {preview.errors}</strong> with errors.
          </p>
          <table className={styles.diffTable}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Period</th>
                <th>Base $</th>
                <th>Overage $</th>
                <th>Other $</th>
                <th>Platform Enh. $</th>
                <th>Total Before → After</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map((row) => {
                const cls =
                  row.errors.length > 0
                    ? styles.diffError
                    : row.changed
                      ? styles.diffChanged
                      : styles.diffOk
                const fmt = (m) => (m == null ? '—' : `$${(parseInt(m, 10) / 100).toFixed(2)}`)
                const stateText = row.errors.length
                  ? row.errors.join('; ')
                  : row.after?.overage_action === 'void'
                    ? 'Will update base + void overage'
                    : row.after?.overage_action === 'create'
                      ? 'Will update base + create new overage draft'
                      : row.after?.overage_action === 'revive'
                        ? 'Will update base + revive cancelled overage'
                        : row.changed
                          ? 'Will update + re-push (stays draft)'
                          : 'No change — Stripe re-pushed (stays draft)'
                return (
                  <tr key={rowKey(row.campaign_id, row.period_start)} className={cls}>
                    <td>#{row.campaign_id}</td>
                    <td>{row.period_start}</td>
                    <td>
                      {fmt(row.before?.base_subtotal_minor)} → {fmt(row.after.base_subtotal_minor)}
                    </td>
                    <td>
                      {fmt(row.before?.overage_subtotal_minor)} →{' '}
                      {fmt(row.after.overage_subtotal_minor)}
                    </td>
                    <td>
                      {fmt(row.before?.other_minor)} → {fmt(row.after.other_minor)}
                    </td>
                    <td>
                      {fmt(row.before?.surcharge_minor)} → {fmt(row.after.surcharge_minor)}
                    </td>
                    <td>
                      {fmt(row.before?.total_post_minor)} → {fmt(row.after.total_post_minor)}
                    </td>
                    <td>{stateText}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className={styles.opsDialogActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={runCommit}
              disabled={submitting || preview.errors === preview.total}
              data-testid="ops-reupload-confirm"
            >
              {submitting
                ? `Committing ${progress?.processed ?? 0} / ${progress?.total ?? preview.total}…`
                : `Confirm — Update + Finalize ${preview.total - preview.errors} campaigns`}
            </button>
          </div>
        </>
      )}

      {results && (
        <>
          {progress && progress.total > 0 && (
            <div
              className={styles.progressWrapper}
              role="progressbar"
              aria-valuenow={progress.processed}
              aria-valuemin={0}
              aria-valuemax={progress.total}
            >
              <div className={styles.progressLabel}>
                {submitting
                  ? `Processing ${progress.processed} / ${progress.total}…`
                  : `Done — ${progress.processed} / ${progress.total}`}
              </div>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressBarFill}
                  style={{ width: `${Math.round((progress.processed / progress.total) * 100)}%` }}
                />
              </div>
            </div>
          )}

          <table className={styles.resultsTable}>
            <thead>
              <tr>
                <th>Campaign</th>
                <th>Period</th>
                <th>Status</th>
                <th>Overage</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {(preview?.rows || []).map((previewRow) => {
                const key = rowKey(previewRow.campaign_id, previewRow.period_start)
                const result = results.get(key)
                const status = result?.status
                // Only show the overage verdict for rows that actually committed.
                // Failed/rejected rows shouldn't read as 'voided' or 'updated'.
                const overageAction =
                  status === 'committed' || status === 'partial'
                    ? result?.after?.overage_action
                    : null
                const detailText = result
                  ? result.errors?.length
                    ? result.errors.join('; ')
                    : result.base_error || result.overage_error || ''
                  : ''

                let rowCls = styles.resultRow
                if (status === 'failed' || status === 'rejected') {
                  rowCls = `${styles.resultRow} ${styles.resultRowError}`
                } else if (status === 'partial') {
                  rowCls = `${styles.resultRow} ${styles.resultRowError}`
                } else if (status === 'committed') {
                  rowCls = `${styles.resultRow} ${styles.resultRowOk}`
                } else {
                  rowCls = `${styles.resultRow} ${styles.resultRowPending}`
                }

                return (
                  <tr key={key} className={rowCls}>
                    <td className={styles.resultIdCell}>
                      <strong>#{previewRow.campaign_id}</strong>
                    </td>
                    <td>{previewRow.period_start}</td>
                    <td>
                      <CommitStatusPill status={status} />
                    </td>
                    <td>
                      {overageAction === 'void'
                        ? 'voided'
                        : overageAction === 'create'
                          ? 'created'
                          : overageAction === 'revive'
                            ? 'revived'
                            : overageAction === 'update'
                              ? 'updated'
                              : '—'}
                    </td>
                    <td className={styles.resultDetailCell}>
                      {detailText ? (
                        <span title={detailText}>{detailText}</span>
                      ) : (
                        <span className={styles.resultDetailEmpty}>—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>

          <div className={styles.opsDialogActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={onClose}
              disabled={submitting}
            >
              {submitting ? 'Working…' : 'Close'}
            </button>
          </div>
        </>
      )}
    </div>
  )
}
ReuploadDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

/**
 * Operations bar for the Pending Review queue. Hosts:
 *  - Generate monthly invoices (manual May-1 backup)
 *  - Export drafts (CSV)
 *  - Re-upload tuned CSV (preview → commit)
 *  - Export manual ACH file (decrypted bank details)
 */
/**
 * Tiny color-coded pill showing where a row is in the commit lifecycle.
 *  pending → committed | failed | rejected
 */
const CommitStatusPill = ({ status }) => {
  if (!status) {
    return (
      <span className={`${styles.statusPill} ${styles.statusPillPending}`}>
        <FontAwesomeIcon icon="circle-notch" spin /> queued
      </span>
    )
  }
  if (status === 'committed') {
    return (
      <span className={`${styles.statusPill} ${styles.statusPillOk}`}>
        <FontAwesomeIcon icon="check-circle" /> committed
      </span>
    )
  }
  if (status === 'rejected') {
    return (
      <span className={`${styles.statusPill} ${styles.statusPillError}`}>
        <FontAwesomeIcon icon="ban" /> rejected
      </span>
    )
  }
  if (status === 'partial') {
    return (
      <span className={`${styles.statusPill} ${styles.statusPillError}`}>
        <FontAwesomeIcon icon="exclamation-triangle" /> partial
      </span>
    )
  }
  return (
    <span className={`${styles.statusPill} ${styles.statusPillError}`}>
      <FontAwesomeIcon icon="exclamation-triangle" /> failed
    </span>
  )
}
CommitStatusPill.propTypes = { status: PropTypes.string }

const NoRecipientDialog = ({ onClose, onComplete, showToast }) => {
  const [rows, setRows] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeRow, setActiveRow] = useState(null)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const fetchList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingInvoicesNoRecipient)
      const payload = res?.data || res
      setRows(payload?.rows || [])
    } catch (err) {
      logger.error('Failed to load no-recipient list', err)
      showToast(err.message || 'Failed to load list', 'error')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [showToast])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  const submit = useCallback(async () => {
    if (!activeRow) return
    setSubmitting(true)
    try {
      const res = await authenticatedFetchJson(
        apiEndpoints.billingInvoiceSendManualEmail(activeRow.invoice_id),
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: email.trim(), name: name.trim() || undefined }),
        },
      )
      const summary = res?.data || res || {}
      if (summary.no_recipient || summary.sent === 0) {
        showToast(`Send failed for invoice #${activeRow.invoice_id}`, 'warning')
      } else {
        showToast(
          `Sent ${summary.template_key} to ${email.trim()} for invoice #${activeRow.invoice_id}`,
          'success',
        )
      }
      setActiveRow(null)
      setEmail('')
      setName('')
      await fetchList()
      onComplete()
    } catch (err) {
      logger.error('Manual email send failed', err)
      showToast(err.message || 'Send failed', 'error')
    } finally {
      setSubmitting(false)
    }
  }, [activeRow, email, name, fetchList, onComplete, showToast])

  return (
    <div
      className={styles.dialogBackdrop}
      onClick={onClose}
      role="presentation"
      data-testid="ops-no-recipient-dialog"
    >
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>Invoices with no recipient on file</h2>
        <p className={styles.dialogIntro}>
          These invoices have no active billing contact in our system and no email synced from
          Stripe. Type an email to send the appropriate template (we&apos;ll save it as the
          customer&apos;s fallback so future sends find it automatically).
        </p>

        {loading && <p>Loading…</p>}
        {!loading && rows && rows.length === 0 && (
          <p data-testid="no-recipient-empty">
            No invoices waiting on a manual email. Everything is wired up.
          </p>
        )}
        {!loading && rows && rows.length > 0 && (
          <table className={styles.dialogTable} data-testid="no-recipient-table">
            <thead>
              <tr>
                <th>Invoice</th>
                <th>Client</th>
                <th>Type</th>
                <th>Total</th>
                <th>Last attempt</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.invoice_id}>
                  <td>
                    #{r.invoice_id} <small>{r.invoice_number}</small>
                  </td>
                  <td>{r.client_name}</td>
                  <td>{r.invoice_type}</td>
                  <td>
                    {r.total.toFixed(2)} {r.currency}
                  </td>
                  <td>{r.attempted_at ? new Date(r.attempted_at).toLocaleString() : '—'}</td>
                  <td>
                    <button
                      type="button"
                      className={styles.secondaryButton}
                      onClick={() => {
                        setActiveRow(r)
                        setEmail('')
                        setName('')
                      }}
                      data-testid={`no-recipient-send-${r.invoice_id}`}
                    >
                      Send to email…
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {activeRow && (
          <div className={styles.dialogSubsection}>
            <h3>
              Send to invoice #{activeRow.invoice_id} ({activeRow.client_name})
            </h3>
            <label>
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="finance@client.com"
                data-testid="no-recipient-email-input"
              />
            </label>
            <label>
              Recipient name (optional)
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Finance"
                data-testid="no-recipient-name-input"
              />
            </label>
            <div className={styles.dialogActions}>
              <button
                type="button"
                className={styles.secondaryButton}
                onClick={() => setActiveRow(null)}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={submit}
                disabled={submitting || !email.trim()}
                data-testid="no-recipient-submit-btn"
              >
                {submitting ? 'Sending…' : 'Send'}
              </button>
            </div>
          </div>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Dialog for the HubSpot → campaign_billing_plans sync. Default tab is
 * "Dry Run" — admin clicks Run, sees the would-insert/would-update report,
 * then can switch to Apply to actually write. Apply also flips
 * campaigns.billing_status='active' so the cron picks the campaign up
 * (planService.activateCampaignBilling is called inside the same transaction
 * as the plan INSERT in scripts/data/syncBillingPlansFromHubspot.js).
 */
const PHASE_LABELS = {
  discovering_rate_property: 'Discovering HubSpot rate property…',
  rate_property_discovered: 'Rate property discovered',
  fetching_hubspot_companies: 'Fetching HubSpot companies…',
  fetched_hubspot_companies: 'HubSpot companies loaded',
  loading_campaigns: 'Loading campaigns from local DB…',
}

const HubspotSyncDialog = ({ onClose, onComplete, showToast }) => {
  const [report, setReport] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [forceVersion, setForceVersion] = useState(false)
  const [campaign, setCampaign] = useState('')
  const [mode, setMode] = useState(null)
  const [phase, setPhase] = useState(null)
  const [phaseDetail, setPhaseDetail] = useState(null)
  const [progress, setProgress] = useState(null) // { processed, total }
  // Latest ~25 row events; capped so the table doesn't grow unbounded mid-run
  const [recentRows, setRecentRows] = useState([])
  const [pendingToken, setPendingToken] = useState(null)
  const [tokenExpiresAt, setTokenExpiresAt] = useState(null)
  const [commitProgress, setCommitProgress] = useState(null)
  const [commitRows, setCommitRows] = useState([])
  const [commitOutcome, setCommitOutcome] = useState(null) // { applied, failures }

  const resetRunState = () => {
    setReport(null)
    setRecentRows([])
    setProgress(null)
    setPhase('discovering_rate_property')
    setPhaseDetail(null)
    setPendingToken(null)
    setTokenExpiresAt(null)
    setCommitProgress(null)
    setCommitRows([])
    setCommitOutcome(null)
  }

  const consumePrepareStream = async (response) => {
    if (!response.ok || !response.body) {
      const text = await response.text().catch(() => '')
      throw new Error(text || `Sync failed (${response.status})`)
    }
    let finalReport = null
    let token = null
    let expiresAt = null
    await consumeSseStream(response, (event) => {
      if (event.type === 'phase') {
        setPhase(event.phase)
        if (event.fetchedSoFar != null) {
          setPhaseDetail({
            page: event.page,
            fetchedSoFar: event.fetchedSoFar,
            hasMore: !!event.hasMore,
          })
        } else if (event.count != null) {
          setPhaseDetail({ count: event.count })
        } else if (event.rateProperty != null) {
          setPhaseDetail({ rateProperty: event.rateProperty })
        } else {
          setPhaseDetail(null)
        }
      } else if (event.type === 'start') {
        setProgress({ processed: 0, total: event.total })
        setPhase(null)
        setPhaseDetail(null)
      } else if (event.type === 'row') {
        setProgress({ processed: event.processed, total: event.total })
        setRecentRows((prev) => {
          const next = [...prev, event]
          return next.length > 25 ? next.slice(-25) : next
        })
      } else if (event.type === 'done') {
        finalReport = event.report
      } else if (event.type === 'prepared') {
        finalReport = event.report
        token = event.token
        expiresAt = event.expires_at
      } else if (event.type === 'error') {
        throw new Error(event.error || 'Sync stream error')
      }
    })
    return { report: finalReport, token, expiresAt }
  }

  const runDryRun = useCallback(async () => {
    setSubmitting(true)
    setMode('dryrun')
    resetRunState()
    try {
      const response = await authenticatedFetch(apiEndpoints.billingSyncHubspotPlansStream, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          commit: false,
          force_version: forceVersion,
          campaign: campaign.trim() || null,
        }),
      })
      const { report: finalReport } = await consumePrepareStream(response)
      if (!finalReport) {
        throw new Error('Sync ended without a "done" event — check server logs')
      }
      setReport(finalReport)
      const insertedN = finalReport.inserted?.length || 0
      const updatedN = finalReport.updated?.length || 0
      const severity = (finalReport.skippedInvalid?.length || 0) > 0 ? 'warning' : 'success'
      showToast(`Dry-run: would insert ${insertedN}, update ${updatedN}`, severity)
    } catch (err) {
      logger.error('hubspot dry-run failed', err)
      showToast(err.message || 'HubSpot sync failed', 'error')
    } finally {
      setSubmitting(false)
      setPhase(null)
      setPhaseDetail(null)
    }
  }, [forceVersion, campaign, showToast])

  const runPrepare = useCallback(async () => {
    setSubmitting(true)
    setMode('prepare')
    resetRunState()
    try {
      const response = await authenticatedFetch(apiEndpoints.billingSyncHubspotPlansPrepareStream, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({
          force_version: forceVersion,
          campaign: campaign.trim() || null,
        }),
      })
      const { report: finalReport, token, expiresAt } = await consumePrepareStream(response)
      if (!finalReport || !token) {
        throw new Error('Prepare ended without a token — check server logs')
      }
      setReport(finalReport)
      setPendingToken(token)
      setTokenExpiresAt(expiresAt)
      const insertedN = finalReport.inserted?.length || 0
      const updatedN = finalReport.updated?.length || 0
      showToast(
        `Preview ready — would insert ${insertedN}, update ${updatedN}. Click Confirm & Write to apply.`,
        'info',
      )
    } catch (err) {
      logger.error('hubspot prepare failed', err)
      showToast(err.message || 'HubSpot prepare failed', 'error')
    } finally {
      setSubmitting(false)
      setPhase(null)
      setPhaseDetail(null)
    }
  }, [forceVersion, campaign, showToast])

  const runCommit = useCallback(async () => {
    if (!pendingToken) return
    setSubmitting(true)
    setMode('commit')
    setCommitProgress(null)
    setCommitRows([])
    setCommitOutcome(null)
    try {
      const response = await authenticatedFetch(apiEndpoints.billingSyncHubspotPlansCommitStream, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ token: pendingToken }),
      })
      if (!response.ok || !response.body) {
        const text = await response.text().catch(() => '')
        throw new Error(text || `Commit failed (${response.status})`)
      }
      let outcome = null
      let serverReport = null
      let serverError = null
      await consumeSseStream(response, (event) => {
        if (event.type === 'commit_start') {
          setCommitProgress({ processed: 0, total: event.total })
        } else if (event.type === 'commit_row') {
          setCommitProgress({ processed: event.processed, total: event.total })
          setCommitRows((prev) => {
            const next = [...prev, event]
            return next.length > 25 ? next.slice(-25) : next
          })
        } else if (event.type === 'commit_done') {
          /* noop — authoritative outcome arrives in the next 'done' frame */
        } else if (event.type === 'done') {
          outcome = { applied: event.applied, failures: event.failures || [] }
          serverReport = event.report
        } else if (event.type === 'error') {
          serverError = event
        }
      })
      if (serverError) {
        if (serverError.code === 'token_not_found') {
          setPendingToken(null)
          setTokenExpiresAt(null)
          throw new Error(serverError.error)
        }
        throw new Error(serverError.error || 'Commit stream error')
      }
      if (!outcome) {
        throw new Error('Commit ended without a "done" event — check server logs')
      }
      setCommitOutcome(outcome)
      setReport(serverReport || report)
      setPendingToken(null)
      setTokenExpiresAt(null)
      const failureCount = outcome.failures.length
      const severity = failureCount > 0 ? 'warning' : 'success'
      showToast(
        failureCount > 0
          ? `Applied ${outcome.applied} — ${failureCount} failed (see details)`
          : `Applied ${outcome.applied} plan write(s) successfully`,
        severity,
      )
      onComplete()
    } catch (err) {
      logger.error('hubspot commit failed', err)
      showToast(err.message || 'HubSpot commit failed', 'error')
    } finally {
      setSubmitting(false)
    }
  }, [pendingToken, report, onComplete, showToast])

  const cancelPreview = useCallback(() => {
    setPendingToken(null)
    setTokenExpiresAt(null)
    setReport(null)
    setRecentRows([])
    setProgress(null)
    setMode(null)
    showToast('Preview discarded — no DB writes were made', 'info')
  }, [showToast])

  const committedRun = mode === 'commit' && !!commitOutcome

  return (
    <div
      className={styles.dialogBackdrop}
      onClick={onClose}
      role="presentation"
      data-testid="ops-hubspot-sync-dialog"
    >
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>
          <FontAwesomeIcon icon="sync-alt" /> Sync billing plans from HubSpot
        </h2>
        <p className={styles.dialogIntro}>
          Pulls plan-money fields from each HubSpot Company and seeds / updates the matching{' '}
          <code>campaign_billing_plans</code> row. On Apply, also flips the campaign to{' '}
          <strong>billing_status=active</strong> so the next monthly cron picks it up.
        </p>

        <div className={styles.dialogSubsection}>
          <label>
            Restrict to single campaign (optional)
            <input
              type="text"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              placeholder="e.g. Acme Law"
              data-testid="ops-hubspot-campaign-input"
              disabled={submitting}
            />
          </label>
          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={forceVersion}
              onChange={(e) => setForceVersion(e.target.checked)}
              data-testid="ops-hubspot-force-version"
              disabled={submitting}
            />
            <span>
              <strong>Force version</strong> — close existing active plan and insert a new one when
              HubSpot values differ. Without this, mismatches are reported but skipped.
            </span>
          </label>
        </div>

        {phase && submitting && (
          <div className={styles.syncPhaseLabel} data-testid="ops-hubspot-phase">
            <FontAwesomeIcon icon="circle-notch" spin />
            <span>
              {PHASE_LABELS[phase] || phase}
              {phaseDetail?.fetchedSoFar != null && (
                <span className={styles.syncPhaseDetail}>
                  {' '}
                  — page {phaseDetail.page}, {phaseDetail.fetchedSoFar} fetched
                  {phaseDetail.hasMore ? ', more pending…' : ''}
                </span>
              )}
              {phaseDetail?.count != null && (
                <span className={styles.syncPhaseDetail}> ({phaseDetail.count} total)</span>
              )}
              {phaseDetail?.rateProperty != null && (
                <span className={styles.syncPhaseDetail}>
                  {' '}
                  — using <code>{phaseDetail.rateProperty}</code>
                </span>
              )}
            </span>
          </div>
        )}

        {progress && progress.total > 0 && (
          <div
            className={styles.progressWrapper}
            role="progressbar"
            aria-valuenow={progress.processed}
            aria-valuemin={0}
            aria-valuemax={progress.total}
          >
            <div className={styles.progressLabel}>
              {submitting
                ? `Processing ${progress.processed} / ${progress.total}…`
                : `Done — ${progress.processed} / ${progress.total}`}
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressBarFill}
                style={{
                  width: `${Math.round((progress.processed / progress.total) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        {recentRows.length > 0 && (
          <div className={styles.syncProgressTable} data-testid="ops-hubspot-progress-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Campaign</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {recentRows.map((row) => (
                  <tr key={`${row.processed}-${row.campaign}`}>
                    <td>{row.processed}</td>
                    <td>{row.campaign}</td>
                    <td>
                      <span
                        className={`${styles.syncRowStatus} ${
                          styles[`syncRowStatus_${row.status}`] || ''
                        }`}
                      >
                        {row.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pendingToken ? (
          <div className={styles.dialogActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={cancelPreview}
              disabled={submitting}
              data-testid="ops-hubspot-discard-btn"
            >
              <FontAwesomeIcon icon="times" /> Discard preview
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={runCommit}
              disabled={submitting}
              data-testid="ops-hubspot-confirm-btn"
            >
              <FontAwesomeIcon icon="check" />{' '}
              {submitting && mode === 'commit' ? 'Writing…' : 'Confirm & Write to DB'}
            </button>
          </div>
        ) : (
          <div className={styles.dialogActions}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={runDryRun}
              disabled={submitting}
              data-testid="ops-hubspot-dryrun-btn"
            >
              <FontAwesomeIcon icon="eye" />{' '}
              {submitting && mode === 'dryrun' ? 'Running…' : 'Run Dry Run'}
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={runPrepare}
              disabled={submitting}
              data-testid="ops-hubspot-apply-btn"
            >
              <FontAwesomeIcon icon="check" />{' '}
              {submitting && mode === 'prepare' ? 'Preparing…' : 'Apply (preview → write)'}
            </button>
          </div>
        )}

        {commitProgress && commitProgress.total > 0 && (
          <div
            className={styles.progressWrapper}
            role="progressbar"
            aria-valuenow={commitProgress.processed}
            aria-valuemin={0}
            aria-valuemax={commitProgress.total}
            data-testid="ops-hubspot-commit-progress"
          >
            <div className={styles.progressLabel}>
              {submitting && mode === 'commit'
                ? `Writing ${commitProgress.processed} / ${commitProgress.total}…`
                : `Wrote ${commitProgress.processed} / ${commitProgress.total}`}
            </div>
            <div className={styles.progressBar}>
              <div
                className={styles.progressBarFill}
                style={{
                  width: `${Math.round((commitProgress.processed / commitProgress.total) * 100)}%`,
                }}
              />
            </div>
          </div>
        )}

        {commitRows.length > 0 && (
          <div className={styles.syncProgressTable} data-testid="ops-hubspot-commit-table">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Campaign</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {commitRows.map((row) => (
                  <tr key={`commit-${row.processed}-${row.campaign}`}>
                    <td>{row.processed}</td>
                    <td>{row.campaign}</td>
                    <td>
                      <span
                        className={`${styles.syncRowStatus} ${
                          styles[`syncRowStatus_${row.status}`] || ''
                        }`}
                        title={row.error || ''}
                      >
                        {row.status.replace(/_/g, ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {report && (
          <div className={styles.hubspotReport} data-testid="ops-hubspot-report">
            {(() => {
              const insertedN = report.inserted?.length || 0
              const updatedN = report.updated?.length || 0
              const invalidN = report.skippedInvalid?.length || 0
              const driftN = report.skippedActivePlanExists?.length || 0
              const wroteSomething = insertedN + updatedN > 0
              const hasIssues = invalidN > 0 || (driftN > 0 && !forceVersion)
              const failureCount = commitOutcome?.failures?.length || 0
              let banner
              if (pendingToken) {
                banner = {
                  tone: hasIssues ? 'warning' : 'ok',
                  icon: hasIssues ? 'exclamation-triangle' : 'eye',
                  title: hasIssues
                    ? 'Preview ready — review issues, then Confirm'
                    : 'Preview ready — review and Confirm to write',
                  body: hasIssues
                    ? `Would insert ${insertedN} and update ${updatedN}. ${invalidN} validation error(s) and ${driftN} drift skip(s) — these will NOT be written. Click "Confirm & Write to DB" to apply only the safe rows, or Discard to abort.`
                    : `Would insert ${insertedN} new plan(s) and update ${updatedN}. Token expires ${tokenExpiresAt ? new Date(tokenExpiresAt).toLocaleTimeString() : 'in 10 minutes'}. Click "Confirm & Write to DB" to apply, or Discard.`,
                }
              } else if (committedRun && failureCount === 0 && wroteSomething) {
                banner = {
                  tone: 'ok',
                  icon: 'check-circle',
                  title: '✓ Wrote to DB — sync complete',
                  body: `Applied ${commitOutcome?.applied || 0} plan write(s). Affected campaigns are now billing_status=active and will be picked up by the next monthly cron.`,
                }
              } else if (committedRun && failureCount === 0 && !wroteSomething) {
                banner = {
                  tone: 'muted',
                  icon: 'check-circle',
                  title: '✓ Sync complete — nothing to write',
                  body: 'All HubSpot-matched campaigns already had plans with the same values. Nothing changed in the DB.',
                }
              } else if (committedRun && failureCount > 0) {
                banner = {
                  tone: 'warning',
                  icon: 'exclamation-triangle',
                  title: 'Sync complete — partial failures',
                  body: `Applied ${commitOutcome?.applied || 0} write(s); ${failureCount} failed. See the table above for per-row errors. Re-run prepare and re-apply once the underlying issue is fixed.`,
                }
              } else if (mode === 'dryrun' && hasIssues) {
                banner = {
                  tone: 'warning',
                  icon: 'exclamation-triangle',
                  title: 'Dry-run complete — review issues',
                  body:
                    invalidN > 0
                      ? `${invalidN} campaign(s) have validation errors that would block the insert. Fix in HubSpot first, or use the campaign filter.`
                      : `${driftN} campaign(s) have an active plan with values different from HubSpot. Tick "Force version" and re-run to replace them.`,
                }
              } else if (mode === 'dryrun') {
                banner = {
                  tone: 'ok',
                  icon: 'eye',
                  title: 'Dry-run complete — looks clean',
                  body: `Would insert ${insertedN} new plan(s) and update ${updatedN}. No DB writes were made. Click "Apply (preview → write)" when you're ready.`,
                }
              } else {
                banner = {
                  tone: 'muted',
                  icon: 'info-circle',
                  title: 'Sync complete',
                  body: `${insertedN} inserted, ${updatedN} updated. Review details below.`,
                }
              }
              return (
                <div
                  className={`${styles.syncBanner} ${styles[`syncBanner_${banner.tone}`] || ''}`}
                  data-testid="ops-hubspot-banner"
                >
                  <FontAwesomeIcon icon={banner.icon} />
                  <div>
                    <div className={styles.syncBannerTitle}>{banner.title}</div>
                    <div className={styles.syncBannerBody}>{banner.body}</div>
                  </div>
                </div>
              )
            })()}

            <h3>Breakdown</h3>
            <div className={styles.hubspotSummaryGrid}>
              <HubspotSummaryStat label="Inserted" value={report.inserted?.length || 0} tone="ok" />
              <HubspotSummaryStat label="Updated" value={report.updated?.length || 0} tone="ok" />
              <HubspotSummaryStat
                label="No HubSpot match"
                value={report.skippedNoMatch?.length || 0}
                tone="muted"
              />
              <HubspotSummaryStat
                label="No billing customer"
                value={report.skippedNoBillingCustomer?.length || 0}
                tone="muted"
              />
              <HubspotSummaryStat
                label="Active plan exists (skipped)"
                value={report.skippedActivePlanExists?.length || 0}
                tone={forceVersion ? 'muted' : 'warning'}
              />
              <HubspotSummaryStat
                label="No value change"
                value={report.skippedNoChange?.length || 0}
                tone="muted"
              />
              <HubspotSummaryStat
                label="Validation errors"
                value={report.skippedInvalid?.length || 0}
                tone={(report.skippedInvalid?.length || 0) > 0 ? 'error' : 'muted'}
              />
            </div>
            <HubspotReportDetails report={report} />
          </div>
        )}

        <div className={styles.dialogActions}>
          <button type="button" className={styles.secondaryButton} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

HubspotSyncDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

const HubspotSummaryStat = ({ label, value, tone = 'muted' }) => (
  <div className={`${styles.hubspotStat} ${styles[`hubspotStat_${tone}`] || ''}`}>
    <span className={styles.hubspotStatValue}>{value}</span>
    <span className={styles.hubspotStatLabel}>{label}</span>
  </div>
)
HubspotSummaryStat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  tone: PropTypes.oneOf(['ok', 'warning', 'error', 'muted']),
}

const formatMoney = (minor, currency) => {
  if (minor == null) return '—'
  return `${(minor / 100).toFixed(2)}${currency ? ' ' + currency : ''}`
}

const HubspotReportDetails = ({ report }) => {
  const sections = [
    { key: 'inserted', label: 'Would insert', rows: report.inserted, tone: 'ok' },
    { key: 'updated', label: 'Would version', rows: report.updated, tone: 'ok' },
    {
      key: 'skippedActivePlanExists',
      label: 'Drift — needs Force version',
      rows: report.skippedActivePlanExists,
      tone: 'warning',
    },
    {
      key: 'skippedInvalid',
      label: 'Validation errors',
      rows: report.skippedInvalid,
      tone: 'error',
    },
  ].filter((s) => s.rows && s.rows.length > 0)

  if (sections.length === 0) {
    return (
      <p className={styles.hubspotReportEmpty}>
        No campaigns required action under the current run settings.
      </p>
    )
  }

  return sections.map((section) => (
    <details key={section.key} className={styles.hubspotReportSection} open>
      <summary>
        <span className={`${styles.hubspotSectionDot} ${styles[`hubspotStat_${section.tone}`]}`} />
        {section.label} ({section.rows.length})
      </summary>
      <table className={styles.dialogTable}>
        <thead>
          <tr>
            <th>Campaign</th>
            <th>Currency</th>
            <th>Fee</th>
            <th>In-plan / min</th>
            <th>Overage / min</th>
          </tr>
        </thead>
        <tbody>
          {section.rows.map((r, i) => {
            const cur = r.currency || r.incoming?.currency || r.from?.currency
            const fee = r.fee ?? r.incoming?.fee ?? r.to?.fee
            const inPlan = r.inPlanRate ?? r.incoming?.inPlanRate ?? r.to?.inPlanRate
            const overage = r.overageRate ?? r.incoming?.overageRate ?? r.to?.overageRate
            const errs = r.errors?.join('; ')
            return (
              <tr key={i}>
                <td>{r.campaign}</td>
                <td>{cur || '—'}</td>
                <td>{formatMoney(fee, cur)}</td>
                <td>{formatMoney(inPlan, cur)}</td>
                <td>
                  {formatMoney(overage, cur)}
                  {errs && <div className={styles.hubspotInlineError}>{errs}</div>}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </details>
  ))
}
HubspotReportDetails.propTypes = {
  report: PropTypes.object.isRequired,
}

NoRecipientDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
}

const OpsBar = ({ showToast, onComplete }) => {
  const [activeOp, setActiveOp] = useState(null)
  const [exporting, setExporting] = useState(false)
  const [noRecipientCount, setNoRecipientCount] = useState(null)

  const refreshNoRecipientCount = useCallback(async () => {
    try {
      const res = await authenticatedFetchJson(apiEndpoints.billingInvoicesNoRecipient)
      const payload = res?.data || res
      setNoRecipientCount(payload?.count ?? 0)
    } catch (err) {
      logger.error('Failed to fetch no-recipient count', err)
      setNoRecipientCount(null)
    }
  }, [])

  useEffect(() => {
    refreshNoRecipientCount()
  }, [refreshNoRecipientCount])

  const downloadDrafts = useCallback(async () => {
    setExporting(true)
    try {
      const res = await authenticatedFetch(
        `${apiEndpoints.billingInvoicesExportCsv}?status=draft&format=campaign-period`,
      )
      if (!res.ok) {
        const text = await res.text()
        throw new Error(text || `Export failed (${res.status})`)
      }
      const blob = await res.blob()
      downloadBlob(blob, `draft-invoices-${new Date().toISOString().split('T')[0]}.csv`)
      showToast('Draft invoices CSV downloaded', 'success')
    } catch (err) {
      logger.error('draft export failed', err)
      showToast(err.message || 'Export failed', 'error')
    } finally {
      setExporting(false)
    }
  }, [showToast])

  return (
    <>
      <div className={styles.opsCard}>
        <div className={styles.opsSection}>
          <div className={styles.opsSectionHeader}>
            <FontAwesomeIcon icon="briefcase" className={styles.opsSectionIcon} />
            <div>
              <h4 className={styles.opsSectionTitle}>Plan Setup</h4>
              <p className={styles.opsSectionHint}>
                Sync billing plans from HubSpot. Dry-run shows what would change without writing.
              </p>
            </div>
          </div>
          <div className={styles.opsSectionButtons}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setActiveOp('hubspot-sync')}
              data-testid="ops-hubspot-sync-btn"
            >
              <FontAwesomeIcon icon="sync-alt" /> Sync from HubSpot
            </button>
          </div>
        </div>

        <div className={styles.opsSectionDivider} />

        <div className={styles.opsSection}>
          <div className={styles.opsSectionHeader}>
            <FontAwesomeIcon icon="file-invoice" className={styles.opsSectionIcon} />
            <div>
              <h4 className={styles.opsSectionTitle}>Invoice Cycle</h4>
              <p className={styles.opsSectionHint}>
                Generate drafts, export the CSV for review, reupload tuned numbers to commit.
              </p>
            </div>
          </div>
          <div className={styles.opsSectionButtons}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setActiveOp('generate')}
              data-testid="ops-generate-btn"
            >
              <FontAwesomeIcon icon="play-circle" /> Generate Invoices
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={downloadDrafts}
              disabled={exporting}
              data-testid="ops-export-drafts-btn"
            >
              <FontAwesomeIcon icon="download" /> {exporting ? 'Exporting…' : 'Export Drafts'}
            </button>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setActiveOp('reupload')}
              data-testid="ops-reupload-btn"
            >
              <FontAwesomeIcon icon="upload" /> Re-upload Tuned CSV
            </button>
          </div>
        </div>

        <div className={styles.opsSectionDivider} />

        <div className={styles.opsSection}>
          <div className={styles.opsSectionHeader}>
            <FontAwesomeIcon icon="paper-plane" className={styles.opsSectionIcon} />
            <div>
              <h4 className={styles.opsSectionTitle}>Exports &amp; Communications</h4>
              <p className={styles.opsSectionHint}>
                Pull manual ACH charge files, fix invoices that need an admin-supplied email.
              </p>
            </div>
          </div>
          <div className={styles.opsSectionButtons}>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => setActiveOp('manual-ach')}
              data-testid="ops-manual-ach-btn"
            >
              <FontAwesomeIcon icon="university" /> Export Manual ACH File
            </button>
            <button
              type="button"
              className={
                noRecipientCount > 0
                  ? `${styles.secondaryButton} ${styles.warningButton}`
                  : styles.secondaryButton
              }
              onClick={() => setActiveOp('no-recipient')}
              data-testid="ops-no-recipient-btn"
            >
              <FontAwesomeIcon icon="envelope-open" /> Missing Emails
              {noRecipientCount != null && noRecipientCount > 0 && (
                <span className={styles.badge} data-testid="ops-no-recipient-count">
                  {' '}
                  {noRecipientCount}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      {activeOp === 'generate' && (
        <GenerateDialog
          onClose={() => setActiveOp(null)}
          onComplete={() => {
            setActiveOp(null)
            onComplete()
          }}
          showToast={showToast}
        />
      )}
      {activeOp === 'reupload' && (
        <ReuploadDialog
          onClose={() => {
            setActiveOp(null)
            onComplete()
          }}
          onComplete={onComplete}
          showToast={showToast}
        />
      )}
      {activeOp === 'manual-ach' && (
        <ManualAchDialog onClose={() => setActiveOp(null)} showToast={showToast} />
      )}
      {activeOp === 'no-recipient' && (
        <NoRecipientDialog
          onClose={() => {
            setActiveOp(null)
            refreshNoRecipientCount()
          }}
          onComplete={() => {
            refreshNoRecipientCount()
            onComplete()
          }}
          showToast={showToast}
        />
      )}
      {activeOp === 'hubspot-sync' && (
        <HubspotSyncDialog
          onClose={() => setActiveOp(null)}
          onComplete={onComplete}
          showToast={showToast}
        />
      )}
    </>
  )
}

OpsBar.propTypes = {
  showToast: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
}

export default OpsBar
